import React, { useEffect, useRef } from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';
import Select, { SelectInstance } from 'react-select';

import flagSprite from '@lt/components/components/Flags';
import { DefaultCountriesOption, DestinationOption } from '@lt/api/dist/methods/references/types';

import { getIsFeatureHasAnOption, isIOS } from '../../../../utils';
import { SEARCH_TYPE_NAMES } from '../../constants';
import { useEventListener } from 'usehooks-ts';
import { ArrowLeftIcon } from '../BackWizardButton/BackWizardButton';
import {
  mobileDefaultControlStyles,
  mobileHotelPageControlStyles,
  MobileLabel,
} from '../../styles';
import { DestinationPickerProps } from './types';
import Control from './components/Control';
import Group from './components/Group';
import Option from './components/Option';
import Input from './components/Input';
import { NoOptionsMessage } from './components/NoOptionsMessage/NoOptionsMessage';
import SingleValue from './components/SingleValue';
import ValueContainer from './components/ValueContainer';
import { placeholder, destinationPickerName, DEFAULT_IMAGE } from './constants';
import CloseButton from '../CloseButton';

const FlagSprites = createGlobalStyle`
  ${flagSprite()}
`;

export const DestinationPicker = ({
  options,
  isMobile = false,
  value,
  inputValue,
  onInputChange,
  isWizardOpen,
  isWizard,
  menuIsOpen,
  isDisabled,
  onMenuOpen,
  onMenuClose,
  onChange,
  changeOptions,
  className,
  searchType,
  variant = 'default',
}: DestinationPickerProps) => {
  const ref = useRef<SelectInstance<DestinationOption>>(null);
  const showBackButton = !!menuIsOpen && isWizard;
  const showCloseButton = isMobile && !!menuIsOpen && !isWizard;
  const { image = '' } = (value || {}) as DefaultCountriesOption;

  useEffect(() => {
    if (menuIsOpen && ref.current) {
      ref.current.focus();
      if (isIOS) {
        setTimeout(() => {
          // Хак из-за того, что из-за клавиатуры инпут на айфоне улетает вверх
          // TODO: Убрать это и сделать нормально
          window.scrollTo(0, 0);
        }, 500);
      }
    }
  }, [menuIsOpen]);

  // Если тыкаем где-то за пределами инпута, то скрываем клавиатуру на мобильном устройстве
  useEventListener('touchstart', (event) => {
    const el = ref.current?.controlRef;

    if (!el || el.contains(event.target as Node)) {
      // Хак для Webview, без этого не работает тач по полю
      isWizard && onMenuOpen && onMenuOpen();
      return;
    }

    setTimeout(() => {
      ref.current?.inputRef?.blur();
    }, 200);
  });

  const hasHotelSearch = getIsFeatureHasAnOption('hotel_search', 'on');

  const onFocus = () => value && ref.current?.inputRef?.select();

  return (
    <>
      {isMobile && !isWizard && (
        <>
          <StyledMobileLabel $variant={variant}>{placeholder(true)}</StyledMobileLabel>
          {menuIsOpen && <StyledCloseButton onClick={onMenuClose} />}
        </>
      )}
      <SelectContainer
        $withBackButton={showBackButton}
        $withCloseButton={showCloseButton}
        $isMobile={isMobile}
        $isWizardOpen={isWizardOpen}
        $image={image}
        $isWizard={isWizard}
        $menuIsOpen={menuIsOpen}
        className={className}
        $hasHotelSearch={hasHotelSearch}
        $searchType={searchType}
        $hasVisaLabel={Boolean((value as DefaultCountriesOption)?.visa)}
        $variant={variant}
      >
        <FlagSprites />
        <Select<DestinationOption>
          ref={ref as any}
          onInputChange={onInputChange}
          className={destinationPickerName}
          classNamePrefix={destinationPickerName}
          placeholder={placeholder(menuIsOpen)}
          options={options}
          isClearable
          backspaceRemovesValue
          escapeClearsValue={false}
          inputValue={!isMobile || (isMobile && menuIsOpen) ? inputValue : undefined}
          defaultValue={value}
          onMenuOpen={onMenuOpen}
          filterOption={() => true}
          onMenuClose={isMobile ? undefined : onMenuClose}
          isMobile={isMobile}
          onChange={onChange}
          onFocus={onFocus}
          menuIsOpen={menuIsOpen}
          isDisabled={isDisabled}
          changeOptions={changeOptions}
          isMulti={false}
          components={{
            Control,
            Group,
            Option,
            Input,
            NoOptionsMessage,
            IndicatorSeparator: () => null,
            DropdownIndicator: () => null,
            ClearIndicator: () => null,
            ...(isMobile ? { SingleValue } : { SingleValue: () => null }),
            ValueContainer,
          }}
          isSearchable={!isMobile || menuIsOpen}
        />
      </SelectContainer>
    </>
  );
};

const SelectContainer = styled.div<{
  $withBackButton?: boolean;
  $isMobile: boolean;
  $isWizard?: boolean;
  $isWizardOpen?: boolean;
  $image: string;
  $hasHotelSearch?: boolean;
  $menuIsOpen?: boolean;
  $withCloseButton?: boolean;
  $searchType?: string;
  $hasVisaLabel?: boolean;
  $variant?: 'default' | 'hotel';
}>`
  display: flex;
  position: relative;
  align-items: center;
  width: 100%;
  flex-grow: ${({ $isMobile }) => ($isMobile ? 0 : 2)};
  ${({ $hasHotelSearch, $isMobile }) => (!$hasHotelSearch && !$isMobile ? 'max-width: 600px;' : '')}
  ${({ $isMobile, $isWizard, $menuIsOpen }) =>
    $isMobile &&
    !$isWizard &&
    $menuIsOpen &&
    css`
      position: fixed;
      top: 0;

      &:before {
        content: '';
        top: 0;
        background: black;
      }
    `}

  .${destinationPickerName} {
    width: 100%;
    position: initial;
    background-color: #fff;
    border-radius: 4px 0 0 4px;
    color: #17181a;

    &__control {
      min-width: 200px;
      padding: 8px;
      font-size: 14px;
      line-height: 16px;
      border: 0;
      box-shadow: none;
      height: 56px;
      caret-color: #007aff;
      border-radius: 4px 0 0 4px;
      color: #17181a;
      font-weight: bold;

      &--menu-is-open {
        font-weight: normal;
      }

      &:hover {
        border: 0;
        box-shadow: none;
      }

      &-visa {
        display: none;
      }
    }
    &__option {
      position: relative;
      padding: 0;
      color: inherit;
      width: initial;

      &--is-focused {
        background-color: #e6f2ff;
      }

      &--is-selected {
        background-color: #e6f2ff;
      }
    }

    &__placeholder {
      color: #919499;
      font-weight: normal;
    }

    &__placeholder,
    &__input-container {
      padding: 0;
      margin: 0;
    }

    &__input-container {
      visibility: visible;
    }

    ${({ $isMobile }) =>
      !$isMobile &&
      `
      /* fix small input touch region bug */
      &__input-container {
        display: block;

        & > input {
          display: inline-block;
        }

        &::after {
          content: none;
        }
      }
    `}

    &__menu {
      margin: 0;
      box-shadow: none;
      width: 320px;
      z-index: 9999;
    }

    &__menu-list {
      position: initial;
      max-height: 554px;
      padding: 0;
    }

    &__group-heading {
      text-transform: none;
      font-size: 12px;
      line-height: 16px;
      padding-left: 8px;
      padding-right: 8px;
    }

    &__countries,
    &__popularCountries {
      & .${destinationPickerName}__option::after, .${destinationPickerName}__option::after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        background: linear-gradient(90deg, #ffffff 33.85%, rgba(255, 255, 255, 0) 100%);
      }
      &
        .${destinationPickerName}__option--is-selected::after,
        .${destinationPickerName}__option--is-focused::after {
        background: linear-gradient(90deg, #e6f2ff 33.85%, rgba(230, 242, 255, 0) 100%);
      }
    }

    &__allMetaregions {
      height: 554px;
      border-radius: 4px 0 0 4px;
    }

    &__allCountries {
      width: 750px;
      height: 554px;
      background-color: #fff;
      position: absolute;
      ${({ $searchType }) => $searchType === SEARCH_TYPE_NAMES.PACKAGE && `left: 100%;`}
      top: 0;
      overflow: ${({ $isMobile }) => ($isMobile ? 'auto' : 'hidden')};
      padding: 10px 8px;
      border-radius: 0 4px 4px 0;

      & > :last-child {
        height: inherit;
        display: flex;
        flex-flow: column wrap;
        align-content: space-between;
      }
    }
  }

  ${({ $isMobile, $variant }) =>
    $isMobile &&
    css`
      .${destinationPickerName} {
        border-radius: 4px;

        &__control {
          width: 100%;

          ${mobileDefaultControlStyles};
          ${$variant === 'hotel' && mobileHotelPageControlStyles}
          padding: 0;
        }

        &__menu {
          height: 100vh;
          width: 100%;
          left: 0;

          // зажимаем высоту списка, 64px высота инпута destination в открытом
          &-list {
            max-height: none;
            height: calc(100vh - ${isIOS ? '150px' : '64px'});
          }
        }

        &__placeholder {
          font-weight: normal;
          color: #c2c5cc;
        }

        &__allCountries {
          width: initial;
          position: initial;
          // Отступ, так как из-за адресной строки на мобилке не видно последний элемент
          padding-bottom: 60px;
          height: calc(100vh - ${isIOS ? '150px' : '64px'});
          overflow: initial;

          & > :last-child {
            display: block;
          }

          .${destinationPickerName}__option:last-of-type {
            padding-bottom: 60px;
          }
        }

        &__icon-search {
          display: none;
          stroke: #c2c5cc;
        }
      }
    `};

  ${({ $isWizard, $menuIsOpen }) =>
    $isWizard &&
    css`
      max-height: none;
      & + ${ArrowLeftIcon} {
        stroke: #007aff;
      }

      .${destinationPickerName} {
        &__control {
          height: ${$menuIsOpen ? `48px` : `initial`};
          padding: ${$menuIsOpen ? `initial` : `16px 16px 16px 8px`};
          border: none;

          &--is-disabled {
            background-color: #fff;
          }
        }

        &__icon-search {
          display: initial;
        }

        &__single-value--is-disabled {
          color: inherit;
        }
      }
    `}

  ${({ $withBackButton }) =>
    $withBackButton &&
    css`
      position: fixed;
      top: 0;
      z-index: 1;
      padding: 8px 8px 8px 40px;
      background-color: #f5f4f2;

      .${destinationPickerName} {
        &__control {
          border: none;
        }
      }
    `};

  ${({ $isWizardOpen, $withBackButton, $image, $hasVisaLabel }) =>
    $isWizardOpen &&
    !$withBackButton &&
    css`
      .${destinationPickerName} {
        padding: ${$hasVisaLabel ? '8px 0 18px 0' : '12px 0 24px 0'};
        background: ${$image ? `url(${$image})` : `url(${DEFAULT_IMAGE}) center 45%`};
        background-size: cover;
        border-radius: 0;

        &:after {
          content: '';
          position: absolute;
          padding: 5px;
          background-color: #fff;
          bottom: 0;
          left: 0;
          right: 0;
          border-radius: 8px 8px 0 0;
        }

        &__control {
          width: fit-content;
          height: auto;
          min-width: 0;
          border-radius: 30px;
          margin: auto;
          border: none;
          font-weight: normal;
          padding: 4px 16px 8px;
          display: flex;
          flex-direction: column;

          &-visa {
            display: inline-block;
            font-size: 12px;
            line-height: 16px;
            color: #919499;
          }
        }

        &__single-value,
        &__value-container,
        &__input-container {
          display: flex;
          margin: 0;
          padding: 0;
        }

        &__icon-search {
          display: none;
        }
      }
    `};

  ${({ $withCloseButton }) =>
    $withCloseButton &&
    css`
      position: fixed;
      // Костыль для position fixed на айфон
      transform: translate3d(0, 0, 0);
      top: 0;
      left: 0;
      right: 0;
      z-index: 1;
      padding: 8px 40px 8px 8px;
      background-color: #f5f4f2;

      .${destinationPickerName} {
        &__control {
          border: none;
        }

        &__icon-search {
          display: inline;
          margin-left: 8px;
        }

        &__value-container {
          display: flex;
        }

        &__menu {
          background-color: #f5f4f2;
          border-radius: 0;
          max-height: none;

          &-list {
            max-height: none;
            background-color: #fff;
            border-radius: 8px 8px 0 0;
            height: calc(100vh - ${isIOS ? '150px' : '64px'});
          }
      }
    `};
`;

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const StyledCloseButton = styled(CloseButton).attrs({ $inMobileInput: true })`
  position: fixed;
  margin: 0px;
  right: 12px;
  top: 24px;
`;

const StyledMobileLabel = styled(MobileLabel)`
  margin-bottom: 4px;
`;
